/** A single-slot undo window (UX C4, spec 3.11): `arm(info)` exposes `info`
 *  as `pending` for `durationMs` (default 30 s), after which it silently
 *  expires. `clear()` cancels immediately (call it when the undo is taken).
 *  Re-arming replaces the slot and restarts the timer. The unmount cleanup
 *  clears any live timer. */
import { useCallback, useEffect, useRef, useState } from 'react'

export interface UndoWindow<T> {
  pending: T | null
  arm: (info: T) => void
  clear: () => void
}

export function useUndoWindow<T>(durationMs = 30_000): UndoWindow<T> {
  const [pending, setPending] = useState<T | null>(null)
  const timerRef = useRef<number | null>(null)

  const clear = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    setPending(null)
  }, [])

  const arm = useCallback(
    (info: T) => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current)
      setPending(info)
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        setPending(null)
      }, durationMs)
    },
    [durationMs],
  )

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current)
    },
    [],
  )

  return { pending, arm, clear }
}
